import React from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import Permitted from 'components/core/Permitted.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './EditKpisInstructionsModal.module.scss';

const EditKpisInstructionsModal = ({ tag }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();
  const { val: isOpen, tip: toggle, nay: hide } = useTip(false);

  return (
    <div
      data-bem="EditKpisInstructionsModal"
      data-tag={fromAnyToTrimmed(tag)}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.buttonContainer}>
        <Button variant="icon" onClick={toggle}>
          <div data-bem="KpiSummary__button" className={CN.buttonInner}>
            <Icon variant="questionSolid" width="lg" />
            <span className={CN.text}>{t('humanResources.editKpis.instructionsModal.howToEditButton')}</span>
          </div>
        </Button>
      </div>

      <SalonModal tag="TimeOffSplashUpgradePlan" isOpen={isOpen} variant="medium">
        <SalonModal.Header>
          {t('humanResources.editKpis.instructionsModal.title')}
          <Button variant="empty" onClick={hide}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <Permitted value={PERM.canEditKpisAllStaff}>
            <div className={CN.modalContainer}>
              <ul className={CN.list}>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="drag" width="lg" variantInvertDark />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.dotsInstruction')}
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="info" width="md" variantInvertDark />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.infoInstruction')}
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="delete" width="lg" variantInvertDark />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.binInstruction')}
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="plus" width="lg" />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.addKpiInstruction')}
                </li>
              </ul>

              <span className={CN.dot}>{t('humanResources.editKpis.instructionsModal.bullet1')}</span>
              <span className={CN.dot}>{t('humanResources.editKpis.instructionsModal.bullet2')}</span>
              <span className={CN.dot}>
                {t('humanResources.editKpis.instructionsModal.bullet3.part1')}
                <NavLink
                  to={linkTo({
                    pattern: LINK.settingsPermissions,
                    params: { salonId },
                    fault: '#',
                  })}
                >
                  <span className={CN.link}>{t('humanResources.editKpis.instructionsModal.bullet3.part2')}</span>
                </NavLink>
                {t('humanResources.editKpis.instructionsModal.bullet3.part3')}
              </span>
            </div>
          </Permitted>
          <Permitted value={PERM.canEditKpisPersonal}>
            <div className={CN.modalContainer}>
              <ul className={CN.list}>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="drag" width="lg" variantInvertDark />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.dotsInstruction')}
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="info" width="md" variantInvertDark />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.infoInstruction')}
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="delete" width="lg" variantInvertDark />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.binInstruction')}
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="plus" width="lg" />
                  </div>
                  {t('humanResources.editKpis.instructionsModal.addKpiInstruction')}
                </li>
              </ul>
              <span className={CN.dot}>{t('humanResources.editKpis.instructionsModal.bullet4')}</span>
            </div>
          </Permitted>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

export default EditKpisInstructionsModal;
